import { FormMemberWrapperRef } from "@domain/member/form-member-wrapper/form-member-wrapper";
import { FormTransactionAddonWrapperRef } from "@domain/transaction-addon/form-transaction-addon-wrapper/form-transaction-addon-wrapper";
import { FormTransactionServiceWrapperRef } from "@domain/transaction-service/form-transaction-service-wrapper/form-transaction-service-wrapper";
import {
  MemberFormSchema,
  TransactionAddonFormSchema,
  TransactionServiceFormSchema,
} from "@factory/form/form.rule";
import { GetResponseMemberModel } from "@factory/model/query-model-member";
import { GetResponseServiceModel } from "@factory/model/query-model-service";
import { CreateTransactionWaiverModelExtension } from "@factory/model/query-model-transaction-waiver";
import { GetWaiverModel } from "@factory/model/query-model-waiver";
import { viewAsset } from "@factory/query/google-cloud-storage.query";
import {
  useQyGetAllMember,
  useQyGetMemberById,
  useQyGetParent,
} from "@factory/query/member.query";
import { useQyGetService } from "@factory/query/service.query";
import { LabelValue } from "@shared/models/label-value.interface";
import { sumBy } from "lodash-es";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { isParentAndChildEnabled } from "../../../../app/app.feature";
import { useTransactionHelper } from "../transaction-helper/transaction-helper";
import { useTransactionUtil } from "@core/utility/transaction.helper";
import { useQyGetTransaction } from "@factory/query/transaction.query";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";

export interface TransactionPaymentSummary {
  amount: number;
  quantity: string;
  name: string;
  total: number;
}
export function useTransactionStep() {
  const storagePrefix = typeof window !== "undefined" && window.location.pathname.includes("/subscription")
    ? "new_subscription_flow_state_"
    : "new_booking_flow_state_";

  const getInitialValue = <T,>(key: string, defaultValue: T): T => {
    try {
      const saved = localStorage.getItem(`${storagePrefix}${key}`);
      return saved ? JSON.parse(saved) : defaultValue;
    } catch (e) {
      console.error("Failed to load state from localStorage", e);
      return defaultValue;
    }
  };

  const stepperRef = useRef(null);
  const [serviceList, setServiceList] = useState<LabelValue[]>([]);
  const [childrenList, setChildrenList] = useState<LabelValue[]>([]);
  const [childFirstList, setChildFirstList] = useState<LabelValue[]>([]);
  const [parentList, setParentList] = useState<LabelValue[]>([]);
  const [memberList, setMemberList] = useState<LabelValue[]>([]);

  const isParentAndChild = isParentAndChildEnabled();

  const [ongoingTransactions, setOngoingTransactions] = useState<any[]>([]);
  useQyGetTransaction(
    "",
    999,
    0,
    undefined,
    { status: "ON-GOING" },
    undefined,
    true,
    (response) => {
      setOngoingTransactions(response?.data || []);
    }
  );

  const { getDateTimeLocal } = useDateTimeContext();
  const { combinedServices } = useTransactionHelper();
  const { getEndDateTime } = useTransactionUtil();

  const getActiveMemberEndDateTime = useCallback(
    (memberCode: string): Date | null => {
      if (!memberCode || ongoingTransactions.length === 0) return null;
      const activeServices = ongoingTransactions
        .flatMap((t) => t.service || [])
        .filter(
          (s) =>
            s.member === memberCode &&
            !s.is_checkout &&
            s.is_active !== false &&
            !s.deleted_at
        );
      if (activeServices.length === 0) return null;
      const combined = combinedServices(activeServices);
      const memberCombined = combined.find((s) => s.member === memberCode);
      if (!memberCombined) return null;
      
      const endDateTime = getEndDateTime(memberCombined);
      const now = new Date();
      if (endDateTime.getTime() <= now.getTime()) return null;
      
      return endDateTime;
    },
    [ongoingTransactions, combinedServices, getEndDateTime, getDateTimeLocal]
  );

  const [selectedParent, setSelectedParent] = useState<string>(() =>
    getInitialValue("selectedParent", "")
  );
  const [selectedMember, setSelectedMember] = useState<string>(() =>
    getInitialValue("selectedMember", "")
  );
  const [selectedService, setSelectedService] = useState<string>(() =>
    getInitialValue("selectedService", "")
  );
  const [selectedAddon, setSelectedAddon] = useState<LabelValue[]>(() =>
    getInitialValue("selectedAddon", [])
  );

  const [selectedWaiverForm, setSelectedWaiverForm] =
    useState<CreateTransactionWaiverModelExtension | null>(() =>
      getInitialValue("selectedWaiverForm", null)
    );
  const [selectedWaiverPreview, setSelectedWaiverPreview] =
    useState<GetWaiverModel | null>(() =>
      getInitialValue("selectedWaiverPreview", null)
    );
  const [signaturePreview, setSignaturePreview] = useState<string | null>(() =>
    getInitialValue("signaturePreview", null)
  );

  const [isCreateNewChild, setIsCreateNewChild] = useState<boolean>(() =>
    getInitialValue("isCreateNewChild", false)
  );

  const [activeStep, setActiveStep] = useState<number>(() =>
    getInitialValue("activeStep", 0)
  );

  // Persistent flow states moved from page hooks
  const [isCreateNewParent, setIsCreateNewParent] = useState<boolean>(() =>
    getInitialValue("isCreateNewParent", false)
  );
  const [isCreateNewMember, setIsCreateNewMember] = useState<boolean>(() =>
    getInitialValue("isCreateNewMember", false)
  );
  const [pendingParentForm, setPendingParentForm] = useState<{
    parentForm: MemberFormSchema;
    guardianForm: MemberFormSchema | null;
    addAsGuardian: boolean;
  } | null>(() => getInitialValue("pendingParentForm", null));

  const [subscriptionStartDate, setSubscriptionStartDate] = useState<string | null>(() =>
    getInitialValue("subscriptionStartDate", null)
  );

  const [parentGuardianFormDraft, setParentGuardianFormDraft] = useState<any>(() =>
    getInitialValue("parentGuardianFormDraft", {
      addAsGuardian: true,
      parentFirstName: "",
      parentLastName: "",
      parentMobileNumber: "",
      guardianFirstName: "",
      guardianLastName: "",
      guardianMobileNumber: "",
    })
  );

  const handleGetServiceSuccess = (data: GetResponseServiceModel) => {
    if (!data || data.data?.length === 0) {
      return;
    }

    const responseData = data.data || [];
    const labelValue = LabelValue.MapArray(
      responseData,
      (item) => item.name,
      (item) => item.code
    );
    setServiceList(labelValue);
  };
  useQyGetService(
    "",
    9999,
    0,
    undefined,
    undefined,
    true,
    handleGetServiceSuccess
  );

  const handleGetParentSuccess = (data: GetResponseMemberModel) => {
    if (!data || data.data?.length === 0) {
      return;
    }

    const responseData = data.data || [];
    const labelValue = LabelValue.MapArray(
      responseData,
      (item) => `${item.first_name} ${item.last_name}`,
      (item) => item.code,
      (item) => item
    );
    setParentList(labelValue);
  };
  const { isLoading: isParentListLoading } = useQyGetParent(
    "",
    9999,
    0,
    undefined,
    undefined,
    isParentAndChild,
    handleGetParentSuccess
  );

  const handleGetAllMemberSuccess = (data: GetResponseMemberModel) => {
    if (!data || data.data?.length === 0) {
      return;
    }

    const responseData = data.data || [];
    const labelValue = LabelValue.MapArray(
      responseData,
      (item) => `${item.first_name} ${item.last_name}`,
      (item) => item.code,
      (item) => item
    );
    setMemberList(labelValue);
  };
  const { isLoading: isMemberListLoading } = useQyGetAllMember(
    "",
    9999,
    0,
    undefined,
    undefined,
    !isParentAndChild,
    handleGetAllMemberSuccess
  );

  const handleGetParentChildrenSuccess = async (
    data: GetResponseMemberModel
  ) => {
    if (!data || data.data?.length === 0) {
      return;
    }

    const responseData = data.data?.[0];
    const children = responseData?.child || [];

    const updatedChildren = await Promise.all(
      children.map(async (element) => {
        if (!element.avatar) return element;

        const elementFile = await viewAsset(element.avatar);
        const fileBlob = URL.createObjectURL(elementFile);
        return { ...element, fileBlob };
      })
    );

    const labelValue = LabelValue.MapArray(
      updatedChildren,
      (item) => `${item.first_name} ${item.last_name}`,
      (item) => item.code,
      (item) => item
    );
    setChildrenList(labelValue);
  };
  useQyGetMemberById(
    isParentAndChild ? selectedParent : "",
    handleGetParentChildrenSuccess
  );

  const handleGetAllChildrenSuccess = (
    data: GetResponseMemberModel
  ) => {
    if (!data || data.data?.length === 0) {
      return;
    }

    const responseData = data.data || [];
    const children = responseData.filter((item) => item.is_child);

    const labelValue = LabelValue.MapArray(
      children,
      (item) => `${item.first_name} ${item.last_name}`,
      (item) => item.code,
      (item) => item
    );
    setChildFirstList(labelValue);
  };
  const { isLoading: isChildFirstListLoading } = useQyGetAllMember(
    "",
    9999,
    0,
    undefined,
    undefined,
    isParentAndChild,
    handleGetAllChildrenSuccess
  );

  const nextStepper = () => {
    (stepperRef as any).current.nextCallback();
  };
  const previousStepper = () => {
    (stepperRef as any).current.prevCallback();
  };

  // Addon
  const [addonFormData, setAddonFormData] = useState<
    TransactionAddonFormSchema[]
  >(() => getInitialValue("addonFormData", []));
  const addonTransactionRef = useRef<FormTransactionAddonWrapperRef>(null);
  const handleAddonTrigger = () => {
    if (addonTransactionRef.current) {
      addonTransactionRef.current.triggerSubmit();
    }
  };
  const handleAddonForm = (data: TransactionAddonFormSchema) => {
    setAddonFormData((prev) => [...prev, data]);

    if (addonTransactionRef.current) {
      addonTransactionRef.current.triggerReset();
    }
  };

  // Service
  const [serviceFormCount, setServiceFormCount] = useState(1);
  const [serviceFormData, setServiceFormData] = useState<
    TransactionServiceFormSchema[]
  >(() => getInitialValue("serviceFormData", []));
  const serviceFormRef = useRef<FormTransactionServiceWrapperRef[]>([]);

  const handleServiceTrigger = async () => {
    const activeLength = isParentAndChild
      ? (isCreateNewChild ? childFormData.length : selectedChildren.length)
      : 1;
    const activeRefs = serviceFormRef.current.slice(0, activeLength);
    const results = await Promise.all(
      activeRefs.map(async (ref) => {
        if (!ref) {
          return false;
        }
        return await ref.triggerSubmit();
      })
    );
    return results.every((valid) => valid);
  };
  const handleServiceForm = (
    data: TransactionServiceFormSchema,
    index: number
  ) => {
    setServiceFormData((prev) => {
      const updatedForms = [...prev];
      updatedForms[index] = data; // Update the data for the specific form
      return updatedForms;
    });
  };

  // Parent
  const [parentFormData, setParentFormData] =
    useState<MemberFormSchema | null>(() =>
      getInitialValue("parentFormData", null)
    );
  const parentMemberRef = useRef<FormMemberWrapperRef>(null);
  const handleParentTrigger = () => {
    if (parentMemberRef.current) {
      parentMemberRef.current.triggerSubmit();
    }
  };
  const handleParentForm = (data: MemberFormSchema) => {
    setParentFormData(data);
  };

  // Member (for individual flow)
  const [memberFormData, setMemberFormData] =
    useState<MemberFormSchema | null>(() =>
      getInitialValue("memberFormData", null)
    );
  const memberFormRef = useRef<FormMemberWrapperRef>(null);
  const handleMemberTrigger = () => {
    if (memberFormRef.current) {
      memberFormRef.current.triggerSubmit();
    }
  };
  const handleMemberForm = (data: MemberFormSchema) => {
    setMemberFormData(data);
  };

  // Child
  const [selectedChildren, setSelectedChildren] = useState<string[]>(() =>
    getInitialValue("selectedChildren", [])
  ); // use for existing children based on selected parent
  const [childFormCount, setChildFormCount] = useState<number>(() =>
    getInitialValue("childFormCount", 1)
  ); // Tracks the number of forms
  const [childFormKeys, setChildFormKeys] = useState<string[]>(() => {
    const savedKeys = getInitialValue<string[]>("childFormKeys", []);
    if (savedKeys.length > 0) return savedKeys;
    const count = getInitialValue("childFormCount", 1);
    return Array.from({ length: count }, (_, i) => `form-${i}-${Date.now()}`);
  });
  const [childFormData, setChildFormData] = useState<MemberFormSchema[]>(() =>
    getInitialValue("childFormData", [])
  ); // tracks newly created children
  const childFormRefs = useRef<FormMemberWrapperRef[]>([]); // Stores refs for each form
  const handleAddChildForm = () => {
    setChildFormCount((prev) => prev + 1); // Add a new form by increasing the count
    setChildFormKeys((prev) => [...prev, `form-${prev.length}-${Date.now()}`]);
  };
  const handleRemoveChildForm = (index: number) => {
    setChildFormCount((prev) => Math.max(1, prev - 1));
    setChildFormData((prev) => {
      const updated = [...prev];
      updated.splice(index, 1);
      return updated;
    });
    setChildFormKeys((prev) => {
      const updated = [...prev];
      updated.splice(index, 1);
      return updated;
    });
    if (childFormRefs.current) {
      childFormRefs.current.splice(index, 1);
    }
  };
  const handleChildTrigger = () => {
    childFormRefs.current.forEach((ref, index) => {
      if (!ref) {
        return;
      }

      ref.triggerSubmit(); // Trigger form submission for each form
    });
  };
  const handleChildFormSubmit = (data: MemberFormSchema, index: number) => {
    setChildFormData((prev) => {
      const updatedForms = [...prev];
      updatedForms[index] = data; // Update the data for the specific form
      return updatedForms;
    });
  };

  // Synchronize and filter out stale service data when selected children or members change.
  useEffect(() => {
    setServiceFormData((prev) => {
      if (isParentAndChild) {
        if (isCreateNewChild) {
          const tempKeys = childFormData.map((_, index) => `new-${index}`);
          return prev.filter((item) => item.member && tempKeys.includes(item.member));
        }
        return prev.filter((item) => item.member && selectedChildren.includes(item.member));
      } else {
        return prev.filter((item) => item.member && item.member === selectedMember);
      }
    });
  }, [selectedChildren, selectedMember, isParentAndChild, isCreateNewChild, childFormData]);

  // Persist states to localStorage when any of them change
  useEffect(() => {
    try {
      localStorage.setItem(`${storagePrefix}activeStep`, JSON.stringify(activeStep));
      localStorage.setItem(`${storagePrefix}selectedParent`, JSON.stringify(selectedParent));
      localStorage.setItem(`${storagePrefix}selectedMember`, JSON.stringify(selectedMember));
      localStorage.setItem(`${storagePrefix}selectedService`, JSON.stringify(selectedService));
      localStorage.setItem(`${storagePrefix}isCreateNewChild`, JSON.stringify(isCreateNewChild));
      localStorage.setItem(`${storagePrefix}addonFormData`, JSON.stringify(addonFormData));
      localStorage.setItem(`${storagePrefix}serviceFormData`, JSON.stringify(serviceFormData));
      localStorage.setItem(`${storagePrefix}parentFormData`, JSON.stringify(parentFormData));
      localStorage.setItem(`${storagePrefix}memberFormData`, JSON.stringify(memberFormData));
      localStorage.setItem(`${storagePrefix}selectedChildren`, JSON.stringify(selectedChildren));
      localStorage.setItem(`${storagePrefix}childFormCount`, JSON.stringify(childFormCount));
      localStorage.setItem(`${storagePrefix}childFormKeys`, JSON.stringify(childFormKeys));
      localStorage.setItem(`${storagePrefix}childFormData`, JSON.stringify(childFormData));
      localStorage.setItem(`${storagePrefix}selectedWaiverForm`, JSON.stringify(selectedWaiverForm));
      localStorage.setItem(`${storagePrefix}selectedWaiverPreview`, JSON.stringify(selectedWaiverPreview));
      localStorage.setItem(`${storagePrefix}isCreateNewParent`, JSON.stringify(isCreateNewParent));
      localStorage.setItem(`${storagePrefix}isCreateNewMember`, JSON.stringify(isCreateNewMember));
      localStorage.setItem(`${storagePrefix}pendingParentForm`, JSON.stringify(pendingParentForm));
      localStorage.setItem(`${storagePrefix}subscriptionStartDate`, JSON.stringify(subscriptionStartDate));
      localStorage.setItem(`${storagePrefix}signaturePreview`, JSON.stringify(signaturePreview));
      localStorage.setItem(`${storagePrefix}parentGuardianFormDraft`, JSON.stringify(parentGuardianFormDraft));
    } catch (e) {
      console.error("Failed to save state to localStorage", e);
    }
  }, [
    storagePrefix,
    activeStep,
    selectedParent,
    selectedMember,
    selectedService,
    isCreateNewChild,
    addonFormData,
    serviceFormData,
    parentFormData,
    memberFormData,
    selectedChildren,
    childFormCount,
    childFormKeys,
    childFormData,
    selectedWaiverForm,
    selectedWaiverPreview,
    isCreateNewParent,
    isCreateNewMember,
    pendingParentForm,
    subscriptionStartDate,
    signaturePreview,
    parentGuardianFormDraft,
  ]);

  const clearLocalStorage = useCallback(() => {
    const keys = [
      "activeStep",
      "selectedParent",
      "selectedMember",
      "selectedService",
      "isCreateNewChild",
      "addonFormData",
      "serviceFormData",
      "parentFormData",
      "memberFormData",
      "selectedChildren",
      "childFormCount",
      "childFormKeys",
      "childFormData",
      "selectedWaiverForm",
      "selectedWaiverPreview",
      "isCreateNewParent",
      "isCreateNewMember",
      "pendingParentForm",
      "subscriptionStartDate",
      "signaturePreview",
      "parentGuardianFormDraft",
    ];
    try {
      keys.forEach((key) => {
        localStorage.removeItem(`${storagePrefix}${key}`);
      });
    } catch (e) {
      console.error("Failed to clear localStorage", e);
    }
  }, [storagePrefix]);

  const calculateTransaction = useMemo(() => {
    const serviceAmount = sumBy(serviceFormData, (item) => item.amount ?? 0); // serviceFormData?.amount ?? 0;
    const addonAmount = addonFormData.reduce((cum, item) => {
      const subTotal = cum + item.amount;
      return subTotal;
    }, 0);

    const total = serviceAmount + addonAmount;
    return total;
  }, [serviceFormData, addonFormData, childFormData, selectedChildren]);

  // # For subscription calculation (services only, no addons)
  const calculateSubscription = useMemo(() => {
    const serviceAmount = sumBy(serviceFormData, (item) => item.amount ?? 0);
    return serviceAmount;
  }, [serviceFormData]);

  const getAddonSummary = useCallback(() => {
    const addonSummary = addonFormData.map((xItem) => {
      const { amount, itemData, quantity } = xItem;
      return {
        amount: itemData.amount || 0,
        name: itemData.name,
        quantity: `${quantity}`,
        total: amount,
      };
    }) as TransactionPaymentSummary[];

    return addonSummary;
  }, [addonFormData]);

  return {
    // other
    stepperRef,
    nextStepper,
    previousStepper,
    calculateTransaction,
    getAddonSummary,
    calculateSubscription,

    // NEW: config
    isParentAndChild,

    // parent
    selectedParent,
    parentMemberRef,
    parentFormData,
    parentList,
    setParentFormData,
    setSelectedParent,
    setParentList,
    handleParentForm,
    handleParentTrigger,

    // NEW: member (only for individual flow)
    selectedMember,
    memberFormRef,
    memberFormData,
    memberList,
    setMemberFormData,
    setSelectedMember,
    setMemberList,
    handleMemberForm,
    handleMemberTrigger,
    // child
    childFormData,
    childFormRefs,
    childFormCount,
    childFormKeys,
    childrenList,
    childFirstList,
    selectedChildren,
    setChildrenList,
    setChildFirstList,
    setSelectedChildren,
    handleChildFormSubmit,
    handleAddChildForm,
    handleRemoveChildForm,
    handleChildTrigger,

    // service
    selectedService,
    serviceList,
    serviceFormData,
    serviceFormRef,
    serviceFormCount,
    setServiceFormCount,
    setSelectedService,
    handleServiceTrigger,
    handleServiceForm,
    setServiceList,

    // service
    addonFormData,
    addonTransactionRef,
    selectedAddon,
    setSelectedAddon,
    setAddonFormData,
    handleAddonTrigger,
    handleAddonForm,

    // Waiver
    selectedWaiverForm,
    setSelectedWaiverForm,
    selectedWaiverPreview,
    setSelectedWaiverPreview,
    signaturePreview,
    setSignaturePreview,

    // Child creation state
    isCreateNewChild,
    setIsCreateNewChild,

    // Active step state
    activeStep,
    setActiveStep,

    // Persistent flow states from page hooks
    isCreateNewParent,
    setIsCreateNewParent,
    isCreateNewMember,
    setIsCreateNewMember,
    pendingParentForm,
    setPendingParentForm,
    subscriptionStartDate,
    setSubscriptionStartDate,
    parentGuardianFormDraft,
    setParentGuardianFormDraft,

    // Active member end date helper
    getActiveMemberEndDateTime,
    ongoingTransactions,
    clearLocalStorage,
    isParentListLoading,
    isMemberListLoading,
    isChildFirstListLoading,
  };
}
